// /accounts/$a/tenants/$t/sites — table + create modal.

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";

import { sites } from "../../api/endpoints/sites";
import { adminQueryOptions } from "../../api/adminQueryOptions";
import { qk } from "../../api/queryKeys";
import { EmptyState } from "../../components/ui/EmptyState";
import { ErrorBanner } from "../../components/ui/ErrorBanner";
import { Modal } from "../../components/ui/Modal";
import { Pill } from "../../components/ui/Pill";
import { Table } from "../../components/ui/Table";
import { PageHeader } from "../../components/shell/PageHeader";
import type { SiteOut } from "../../api/types/site";

import { SiteCreateForm } from "./SiteCreateForm";

interface SiteListPageProps {
  accountSlug: string;
  tenantSlug: string;
}

export function SiteListPage({ accountSlug, tenantSlug }: SiteListPageProps) {
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);

  const q = useQuery({
    ...adminQueryOptions,
    queryKey: qk.sites(accountSlug, tenantSlug),
    queryFn: () => sites.list(accountSlug, tenantSlug).then((r) => r.data),
  });

  return (
    <>
      <PageHeader
        title={`Sites · ${tenantSlug}`}
        subtitle="Physical sites under this tenant. Each can optionally bind an HA instance at create-time."
        actions={
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            style={primaryBtn}
          >
            Create site
          </button>
        }
      />

      {q.isPending && <p style={{ color: "var(--text-dim)" }}>Loading…</p>}
      {q.error && <ErrorBanner error={q.error} title="Failed to list sites" />}
      {q.data && (
        <Table<SiteOut>
          columns={[
            {
              key: "slug",
              header: "Slug",
              render: (s) => (
                <Link
                  to="/accounts/$accountSlug/tenants/$tenantSlug/sites/$siteSlug"
                  params={{ accountSlug, tenantSlug, siteSlug: s.slug }}
                  style={{ color: "var(--accent)" }}
                >
                  {s.slug}
                </Link>
              ),
            },
            { key: "display_name", header: "Display name" },
            {
              key: "city",
              header: "Where",
              render: (s) =>
                [s.city, s.country].filter(Boolean).join(", ") || "—",
            },
            {
              key: "ha_instance_id",
              header: "HA bound",
              render: (s) =>
                s.ha_instance_id ? (
                  <Pill tone="ok">bound</Pill>
                ) : (
                  <Pill tone="neutral">unbound</Pill>
                ),
            },
            { key: "status", header: "Status" },
          ]}
          rows={q.data}
          rowKey={(s) => s.id}
          empty={
            <EmptyState
              title="No sites for this tenant yet"
              description="Create one — the form lets you optionally bind an HA instance at create time."
            />
          }
        />
      )}

      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        closeDisabled={creating}
        title={`Create site for ${tenantSlug}`}
        width={640}
      >
        <SiteCreateForm
          accountSlug={accountSlug}
          tenantSlug={tenantSlug}
          onDone={() => setCreateOpen(false)}
          onPendingChange={setCreating}
        />
      </Modal>
    </>
  );
}

const primaryBtn: React.CSSProperties = {
  background: "var(--accent)",
  color: "var(--bg)",
  border: "none",
  padding: "8px 16px",
  borderRadius: 4,
  fontSize: "0.9rem",
  fontWeight: 600,
  cursor: "pointer",
};
